/**
 * Performance Monitoring Utilities
 * 
 * Implements Requirements 11.1, 19.4:
 * - Lighthouse performance score above 90
 * - Track performance metrics (load time, interaction time)
 * - Web vitals reporting (LCP, FID, CLS, FCP, TTFB, INP)
 * 
 * This module provides utilities for monitoring and reporting web performance metrics.
 * It uses the web-vitals library to track Core Web Vitals and other performance metrics.
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

/**
 * Performance thresholds based on Google's Core Web Vitals
 */
export const PERFORMANCE_THRESHOLDS = {
  // Largest Contentful Paint (LCP) - measures loading performance
  LCP: {
    good: 2500, // <= 2.5s is good
    needsImprovement: 4000, // <= 4s needs improvement
  },
  // First Input Delay (FID) - measures interactivity
  FID: {
    good: 100, // <= 100ms is good
    needsImprovement: 300, // <= 300ms needs improvement
  },
  // Cumulative Layout Shift (CLS) - measures visual stability
  CLS: {
    good: 0.1, // <= 0.1 is good
    needsImprovement: 0.25, // <= 0.25 needs improvement
  },
  // First Contentful Paint (FCP) - measures perceived load speed
  FCP: {
    good: 1800, // <= 1.8s is good
    needsImprovement: 3000, // <= 3s needs improvement
  },
  // Time to First Byte (TTFB) - measures server response time
  TTFB: {
    good: 800, // <= 800ms is good
    needsImprovement: 1800, // <= 1.8s needs improvement
  },
  // Interaction to Next Paint (INP) - measures responsiveness
  INP: {
    good: 200, // <= 200ms is good
    needsImprovement: 500, // <= 500ms needs improvement
  },
} as const;

/**
 * Performance rating based on thresholds
 */
export type PerformanceRating = 'good' | 'needs-improvement' | 'poor';

/**
 * Get performance rating for a metric
 */
export function getPerformanceRating(
  metricName: keyof typeof PERFORMANCE_THRESHOLDS,
  value: number
): PerformanceRating {
  const threshold = PERFORMANCE_THRESHOLDS[metricName];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

/**
 * Performance metric data
 */
export interface PerformanceMetric {
  name: string;
  value: number;
  rating: PerformanceRating;
  delta: number;
  id: string;
  navigationType: string;
}

/**
 * Performance report callback
 */
export type PerformanceReportCallback = (metric: PerformanceMetric) => void | Promise<void>;

/**
 * Initialize web vitals monitoring
 * 
 * This function sets up listeners for all Core Web Vitals metrics.
 * Call this once in your application entry point (main.tsx).
 * 
 * @param onReport - Callback function to handle metric reports
 */
export function initPerformanceMonitoring(onReport: PerformanceReportCallback) {
  // Largest Contentful Paint
  onLCP((metric) => {
    reportMetric(metric, onReport);
  });

  // Interaction to Next Paint (replaces FID in web-vitals v3+)
  onINP((metric: Metric) => {
    reportMetric(metric, onReport);
  });

  // Cumulative Layout Shift
  onCLS((metric) => {
    reportMetric(metric, onReport);
  });

  // First Contentful Paint
  onFCP((metric) => {
    reportMetric(metric, onReport);
  });

  // Time to First Byte
  onTTFB((metric) => {
    reportMetric(metric, onReport);
  });

  // Interaction to Next Paint
  onINP((metric) => {
    reportMetric(metric, onReport);
  });
}

/**
 * Report a performance metric
 */
function reportMetric(metric: Metric, onReport: PerformanceReportCallback) {
  const metricName = metric.name as keyof typeof PERFORMANCE_THRESHOLDS;
  const rating = getPerformanceRating(metricName, metric.value);

  const performanceMetric: PerformanceMetric = {
    name: metric.name,
    value: metric.value,
    rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
  };

  onReport(performanceMetric);

  // Log to console in development
  if (import.meta.env.DEV) {
    const emoji = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';
    console.log(
      `${emoji} ${metric.name}:`,
      metric.value.toFixed(2),
      metric.rating,
      `(delta: ${metric.delta.toFixed(2)})`
    );
  }
}

/**
 * Send performance metrics to analytics
 * 
 * This function can be used as the callback for initPerformanceMonitoring
 * to send metrics to your analytics platform (PostHog, Google Analytics, etc.)
 * 
 * Implements Requirement 19.4: Track performance metrics
 */
export async function sendToAnalytics(metric: PerformanceMetric) {
  // Send to PostHog analytics
  try {
    const { trackEvent } = await import('./analytics');
    trackEvent('performance_metric', {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_rating: metric.rating,
      metric_delta: metric.delta,
      metric_id: metric.id,
      navigation_type: metric.navigationType,
      // Round CLS to 3 decimal places, others to 2
      value_rounded: metric.name === 'CLS' 
        ? Math.round(metric.value * 1000) / 1000
        : Math.round(metric.value * 100) / 100,
    });
  } catch (error) {
    // Analytics not initialized or opted out
    if (import.meta.env.DEV) {
      console.debug('Analytics not available for performance tracking');
    }
  }

  // Example: Send to Google Analytics (if gtag is available)
  if (typeof window !== 'undefined' && 'gtag' in window) {
    (window as any).gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_rating: metric.rating,
      non_interaction: true,
    });
  }

  // Example: Send to custom analytics endpoint
  if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
    fetch(import.meta.env.VITE_ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'performance',
        metric: metric.name,
        value: metric.value,
        rating: metric.rating,
        timestamp: Date.now(),
      }),
      keepalive: true,
    }).catch((error) => {
      console.error('Failed to send performance metric:', error);
    });
  }
}

/**
 * Performance budget checker
 * 
 * Checks if performance metrics meet the defined budget.
 * Useful for CI/CD pipelines and performance testing.
 */
export function checkPerformanceBudget(metrics: PerformanceMetric[]): {
  passed: boolean;
  failures: Array<{ metric: string; value: number; threshold: number }>;
} {
  const failures: Array<{ metric: string; value: number; threshold: number }> = [];

  for (const metric of metrics) {
    const metricName = metric.name as keyof typeof PERFORMANCE_THRESHOLDS;
    const threshold = PERFORMANCE_THRESHOLDS[metricName];

    if (metric.value > threshold.needsImprovement) {
      failures.push({
        metric: metric.name,
        value: metric.value,
        threshold: threshold.needsImprovement,
      });
    }
  }

  return {
    passed: failures.length === 0,
    failures,
  };
}

/**
 * Get navigation timing metrics
 * 
 * Returns detailed timing information from the Navigation Timing API.
 */
export function getNavigationTiming() {
  if (typeof window === 'undefined' || !window.performance) {
    return null;
  }

  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (!navigation) return null;

  return {
    // DNS lookup time
    dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
    // TCP connection time
    tcpConnection: navigation.connectEnd - navigation.connectStart,
    // TLS negotiation time
    tlsNegotiation: navigation.secureConnectionStart > 0
      ? navigation.connectEnd - navigation.secureConnectionStart
      : 0,
    // Time to first byte
    ttfb: navigation.responseStart - navigation.requestStart,
    // Response download time
    responseDownload: navigation.responseEnd - navigation.responseStart,
    // DOM processing time
    domProcessing: navigation.domComplete - navigation.domInteractive,
    // Total load time
    totalLoad: navigation.loadEventEnd - navigation.fetchStart,
  };
}

/**
 * Get resource timing metrics
 * 
 * Returns timing information for all resources loaded by the page.
 */
export function getResourceTiming() {
  if (typeof window === 'undefined' || !window.performance) {
    return [];
  }

  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

  return resources.map((resource) => ({
    name: resource.name,
    type: resource.initiatorType,
    duration: resource.duration,
    size: resource.transferSize,
    cached: resource.transferSize === 0 && resource.decodedBodySize > 0,
  }));
}

/**
 * Get memory usage (Chrome only)
 */
export function getMemoryUsage() {
  if (typeof window === 'undefined' || !('memory' in performance)) {
    return null;
  }

  const memory = (performance as any).memory;
  return {
    usedJSHeapSize: memory.usedJSHeapSize,
    totalJSHeapSize: memory.totalJSHeapSize,
    jsHeapSizeLimit: memory.jsHeapSizeLimit,
    usedPercentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
  };
}

/**
 * Track page load time
 * 
 * Implements Requirement 19.4: Track page load times
 */
export function trackPageLoadTime() {
  if (typeof window === 'undefined' || !window.performance) {
    return;
  }

  // Wait for page to fully load
  if (document.readyState === 'complete') {
    reportPageLoadTime();
  } else {
    window.addEventListener('load', reportPageLoadTime);
  }
}

async function reportPageLoadTime() {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (!navigation) return;

  const loadTime = navigation.loadEventEnd - navigation.fetchStart;
  const domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart;
  const domInteractive = navigation.domInteractive - navigation.fetchStart;

  try {
    const { trackEvent } = await import('./analytics');
    trackEvent('page_load_time', {
      load_time: Math.round(loadTime),
      dom_content_loaded: Math.round(domContentLoaded),
      dom_interactive: Math.round(domInteractive),
      navigation_type: navigation.type,
    });
  } catch (error) {
    // Analytics not available
  }

  if (import.meta.env.DEV) {
    console.log('📊 Page Load Time:', {
      loadTime: `${loadTime.toFixed(2)}ms`,
      domContentLoaded: `${domContentLoaded.toFixed(2)}ms`,
      domInteractive: `${domInteractive.toFixed(2)}ms`,
    });
  }
}

/**
 * Track interaction time (time to interactive)
 * 
 * Implements Requirement 19.4: Track interaction times
 */
export async function trackInteractionTime(interactionName: string, startTime: number) {
  const endTime = performance.now();
  const duration = endTime - startTime;

  try {
    const { trackEvent } = await import('./analytics');
    trackEvent('interaction_time', {
      interaction_name: interactionName,
      duration: Math.round(duration),
      timestamp: Date.now(),
    });
  } catch (error) {
    // Analytics not available
  }

  if (import.meta.env.DEV) {
    console.log(`⚡ ${interactionName}:`, `${duration.toFixed(2)}ms`);
  }

  return duration;
}

/**
 * Create a performance mark
 */
export function mark(name: string) {
  if (typeof window !== 'undefined' && window.performance) {
    performance.mark(name);
  }
}

/**
 * Measure performance between two marks
 */
export function measure(name: string, startMark: string, endMark: string) {
  if (typeof window === 'undefined' || !window.performance) {
    return null;
  }

  try {
    performance.measure(name, startMark, endMark);
    const measure = performance.getEntriesByName(name, 'measure')[0];
    return measure ? measure.duration : null;
  } catch (error) {
    console.error('Failed to measure performance:', error);
    return null;
  }
}

/**
 * Hook for tracking component render time
 */
export function usePerformanceTracking(componentName: string) {
  if (typeof window === 'undefined') return;

  const startTime = performance.now();

  return () => {
    trackInteractionTime(`${componentName}_render`, startTime);
  };
}
